import { isDefined } from '@bcf-vanilla-ts-v1-shared/misc/pure-utils/is-defined';
import type { UseStore } from 'idb-keyval';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

/**
 * In-memory stand-in for IndexedDB when running under Node (`isSSR()`), e.g. Angular SSR worker.
 * Store handles are deterministic strings so repeated `createStore(name)` hits the same bucket
 * (mirrors browser `IndexdbStorage` naming: `${appId}:${lang}:${storeName}`).
 */
export class IndexdbStorageServer {
  private readonly _didUpdate$: Subject<void> = new Subject<void>();
  private readonly _byStoreHandle: Map<string, Map<string, unknown>> = new Map();

  constructor(
    private readonly _bcfAppId: string,
    private readonly _lang: string
  ) {}

  public async createStore(storeName: string): Promise<UseStore> {
    const handle: string = `${this._bcfAppId}:${this._lang}:${storeName}`;
    if (!this._byStoreHandle.has(handle)) {
      this._byStoreHandle.set(handle, new Map());
    }
    return handle as unknown as UseStore;
  }

  public async set<T>(key: IDBValidKey, value: T, store?: UseStore): Promise<void> {
    this._bucket(store).set(this._stringKey(key), value as unknown);
    this._didUpdate$.next(undefined);
  }

  public async delete(key: IDBValidKey, store?: UseStore): Promise<void> {
    this._bucket(store).delete(this._stringKey(key));
    this._didUpdate$.next(undefined);
  }

  public async clear(store?: UseStore): Promise<void> {
    const handle: string | undefined = store as unknown as string | undefined;
    if (handle) {
      this._byStoreHandle.set(handle, new Map());
    } else {
      this._byStoreHandle.clear();
    }
    this._didUpdate$.next(undefined);
  }

  public async get<T>(key: IDBValidKey, store?: UseStore): Promise<T | undefined> {
    return this._bucket(store).get(this._stringKey(key)) as T | undefined;
  }

  public async getMany<T>(keys: IDBValidKey[], store?: UseStore): Promise<T[]> {
    const bucket: Map<string, unknown> = this._bucket(store);
    const results: (T | undefined)[] = keys.map((k: IDBValidKey) => bucket.get(this._stringKey(k)) as T | undefined);
    return results.filter(isDefined);
  }

  public async getAllValues<T>(store?: UseStore): Promise<T[]> {
    const bucket: Map<string, unknown> = this._bucket(store);
    return [...bucket.values()].filter(isDefined) as T[];
  }

  public async setMany<T>(entries: [IDBValidKey, T][], store?: UseStore): Promise<void> {
    const bucket: Map<string, unknown> = this._bucket(store);
    for (const [key, value] of entries) {
      bucket.set(this._stringKey(key), value as unknown);
    }
    this._didUpdate$.next(undefined);
  }

  public didUpdated(): Observable<void> {
    return this._didUpdate$.asObservable();
  }

  private _bucket(store: UseStore | undefined): Map<string, unknown> {
    const handle: string = (store as unknown as string | undefined) ?? '';
    const existing: Map<string, unknown> | undefined = this._byStoreHandle.get(handle);
    if (existing) {
      return existing;
    }
    const created: Map<string, unknown> = new Map();
    this._byStoreHandle.set(handle, created);
    return created;
  }

  private _stringKey(key: IDBValidKey): string {
    if (typeof key === 'string') {
      return key;
    }
    if (typeof key === 'number') {
      return String(key);
    }
    return JSON.stringify(key);
  }
}
